import { supabase } from '@/integrations/supabase/client';

/**
 * Invoke a Supabase Edge Function with AbortController support.
 * The built-in supabase.functions.invoke doesn't support AbortSignal,
 * so we call the function URL directly with fetch.
 */
export async function invokeWithAbort<T = any>(
  functionName: string,
  body: Record<string, any>,
  signal: AbortSignal
): Promise<{ data: T | null; error: any }> {
  const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
  const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY || import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

  const url = `${SUPABASE_URL}/functions/v1/${functionName}`;

  // Get current session token for auth
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData?.session?.access_token;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'apikey': SUPABASE_KEY,
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal,
    });

    if (!response.ok) {
      let parsedBody: unknown = null;
      try {
        parsedBody = await response.json();
      } catch {
        // body not JSON — leave null
      }
      const error: any = new Error('Edge Function returned a non-2xx status code');
      error.name = 'FunctionsHttpError';
      error.status = response.status;
      error.parsedBody = parsedBody;
      error.context = response;
      return { data: null, error };
    }

    const data = await response.json();
    return { data, error: null };
  } catch (err: any) {
    if (err.name === 'AbortError') {
      return { data: null, error: err };
    }
    return { data: null, error: err };
  }
}
